import classNames from 'classnames';
import React, {
  memo, useCallback, useEffect, useRef, useState,
} from 'react';
import ReactCustomScrollBar from 'react-custom-scrollbars';
import { WINDOW_CONTENT_HEIGHT } from '@/constants/window-screen';
import './style.sass';

const renderViewScrollCustom = (e) => <div {...e} className="scroll-wrapper__view" />;
const renderThumbScrollCustom = (e) => <div {...e} className="scroll-wrapper__thumb-vertical" />;

const renderTrackScrollCustom =
  (autoHideHover) =>
    (style, ...e) =>
    (
      <div
        {...e}
        style={{ ...style }}
        className={classNames('scroll-wrapper__track-vertical', {
          'scroll-wrapper__track-vertical--auto-hide-hover': autoHideHover,
        })}
      />
    );

/**
 *
 * @param children {JSX.Element | array | string}
 * @param refCustom {function}
 * @param autoHideHover {boolean}
 * @param heightIs {string | number}
 * @param className {string}
 * @param onUpdate {function}
 * @returns {JSX.Element}
 * @constructor
 */

const ScrollWrapperComponent = ({
  children,
  refCustom = () => { },
  autoHideHover = false,
  heightIs = '',
  className = '',
  calcContentHeight = false,
  onUpdate = () => { },
  scrollRestorationKey = '',
  ...props
}) => {
  const contentsHeight = calcContentHeight ? WINDOW_CONTENT_HEIGHT.LARG_SCREEN : '100%';
  const stylesCustom = { height: heightIs || contentsHeight || '100%', width: '100%' };
  const [isVisible, setIsVisible] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const customClassName = classNames('scroll-wrapper', className, {
    'scroll-wrapper--hidden': !isVisible,
  });

  const scrollbarRef = useRef(null);
  const refCustomRef = useRef(refCustom);

  useEffect(() => {
    refCustomRef.current = refCustom;
  }, [refCustom]);

  const mergedRef = useCallback((ref) => {
    scrollbarRef.current = ref;
    if (refCustomRef.current) {
      refCustomRef.current(ref);
    }
  }, []);

  useEffect(() => {
    if (!scrollRestorationKey) return undefined;

    let saved = null;
    try {
      saved = sessionStorage.getItem(scrollRestorationKey);
    } catch (e) {
      // ignore sessionStorage errors
    }
    if (saved !== null) {
      setRestoreTarget(Number(saved));
      sessionStorage.removeItem(scrollRestorationKey);
    }

    return () => {
      if (scrollbarRef.current) {
        try {
          sessionStorage.setItem(scrollRestorationKey, scrollbarRef.current.getScrollTop());
        } catch (e) {
          // ignore sessionStorage errors
        }
      }
    };
  }, [scrollRestorationKey]);

  const onVisibleStatus = useCallback(
    ({ clientHeight, scrollHeight }) => {
      const newIsVisibleStatus = clientHeight !== scrollHeight;
      if (isVisible !== newIsVisibleStatus) {
        setIsVisible(!isVisible);
      }
    },
    [isVisible],
  );

  const onCustomUpdate = (e) => {
    if (onVisibleStatus) onVisibleStatus(e);
    if (onUpdate) onUpdate(e);
    if (
      restoreTarget !== null
      && e.scrollHeight > e.clientHeight
      && scrollbarRef.current
    ) {
      const maxScroll = e.scrollHeight - e.clientHeight;
      scrollbarRef.current.scrollTop(Math.min(restoreTarget, maxScroll));
      setRestoreTarget(null);
    }
  };

  return (
    <ReactCustomScrollBar
      className={customClassName}
      ref={mergedRef}
      renderView={renderViewScrollCustom}
      onUpdate={onCustomUpdate}
      renderTrackVertical={renderTrackScrollCustom(autoHideHover)}
      renderThumbVertical={renderThumbScrollCustom}
      style={stylesCustom}
      {...props}
    >
      {children}
    </ReactCustomScrollBar>
  );
};

ScrollWrapperComponent.displayName = 'ScrollWrapper';

export const ScrollWrapper = memo(ScrollWrapperComponent);
